import React, { useState, useEffect } from 'react';

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState('loading'); // loading, fadeOut, done

  useEffect(() => {
    // Show splash for 4 seconds
    const timer1 = setTimeout(() => {
      setPhase('fadeOut');
    }, 3500);

    const timer2 = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onComplete]);

  if (phase === 'done') return null;

  // Letters for "Zuiver" with individual animation
  const zuiverLetters = ['Z', 'u', 'i', 'v', 'e', 'r'];

  return (
    <div 
      className={`splash-screen ${phase === 'fadeOut' ? 'animate-fade-out' : ''}`}
      data-testid="splash-screen"
    >
      <div className="logo-container">
        <div className="logo-shimmer">
          {/* ZuiverParfum Luxury Logo */}
          <div className="flex flex-col items-center animate-fade-in">
            {/* Logo Icon - Elegant Perfume Bottle */}
            <div className="relative mb-6">
              <svg 
                width="80" 
                height="140" 
                viewBox="0 0 80 140" 
                className="drop-shadow-2xl logo-glow"
              >
                <defs>
                  {/* Main Gold Gradient */}
                  <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#B8860B" />
                    <stop offset="25%" stopColor="#D4AF37" />
                    <stop offset="50%" stopColor="#FFD700" />
                    <stop offset="75%" stopColor="#D4AF37" />
                    <stop offset="100%" stopColor="#B8860B" />
                  </linearGradient>
                  
                  {/* Shimmer Gradient for animation */}
                  <linearGradient id="shimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#D4AF37">
                      <animate attributeName="stop-color" 
                        values="#D4AF37;#FFE566;#FFFACD;#FFE566;#D4AF37" 
                        dur="3s" 
                        repeatCount="indefinite" />
                    </stop>
                    <stop offset="50%" stopColor="#FFD700">
                      <animate attributeName="stop-color" 
                        values="#FFD700;#FFFACD;#FFD700;#D4AF37;#FFD700" 
                        dur="3s" 
                        repeatCount="indefinite" />
                    </stop>
                    <stop offset="100%" stopColor="#D4AF37">
                      <animate attributeName="stop-color" 
                        values="#D4AF37;#FFD700;#FFFACD;#FFE566;#D4AF37" 
                        dur="3s" 
                        repeatCount="indefinite" />
                    </stop>
                  </linearGradient>
                  
                  {/* Glow filter */}
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Elegant Perfume Bottle */}
                <g filter="url(#glow)">
                  {/* Bottle Cap - Elegant faceted design */}
                  <rect x="28" y="5" width="24" height="8" rx="2" fill="url(#shimmerGradient)" />
                  <rect x="30" y="13" width="20" height="4" fill="url(#goldGradient)" />
                  
                  {/* Bottle Neck */}
                  <rect x="33" y="17" width="14" height="10" fill="url(#goldGradient)" />
                  
                  {/* Neck Ornament Ring */}
                  <ellipse cx="40" cy="27" rx="10" ry="3" fill="url(#shimmerGradient)" />
                  
                  {/* Main Bottle Body - Elegant curved shape */}
                  <path 
                    d="M33 30 
                       L33 38 
                       Q15 45 15 65 
                       L15 120 
                       Q15 130 25 130 
                       L55 130 
                       Q65 130 65 120 
                       L65 65 
                       Q65 45 47 38 
                       L47 30 Z" 
                    fill="url(#goldGradient)"
                    opacity="0.95"
                  />
                  
                  {/* Bottle Inner Liquid Effect */}
                  <path 
                    d="M20 70 
                       L20 117 
                       Q20 125 28 125 
                       L52 125 
                       Q60 125 60 117 
                       L60 70 
                       Q40 80 20 70 Z" 
                    fill="url(#shimmerGradient)"
                    opacity="0.4"
                  />
                  
                  {/* Bottle Shine Lines */}
                  <path 
                    d="M25 55 Q28 90 25 120" 
                    stroke="rgba(255,255,255,0.5)" 
                    strokeWidth="2" 
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path 
                    d="M30 50 Q32 85 30 115" 
                    stroke="rgba(255,255,255,0.3)" 
                    strokeWidth="1" 
                    fill="none"
                    strokeLinecap="round"
                  />
                  
                  {/* Decorative Diamond Emblem on bottle */}
                  <path 
                    d="M40 75 L47 85 L40 95 L33 85 Z" 
                    fill="url(#shimmerGradient)"
                    stroke="#FFFACD"
                    strokeWidth="0.5"
                    className="diamond-sparkle"
                  />
                  
                  {/* Small accent dots */}
                  <circle cx="40" cy="85" r="2" fill="#FFFACD" className="jewel-glow" />
                </g>
              </svg>
            </div>
            
            {/* Brand Name with Letter-by-Letter Shimmer */}
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-2 flex items-baseline">
              {/* Zuiver - Each letter animates individually */}
              <span className="zuiver-container">
                {zuiverLetters.map((letter, index) => (
                  <span 
                    key={index} 
                    className="zuiver-letter"
                    style={{ 
                      animationDelay: `${index * 0.5}s`,
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </span>
              <span className="text-white">Parfum</span>
            </h1>
            
            {/* Tagline */}
            <p className="font-accent text-xs tracking-[0.3em] text-[#A8A29E] uppercase mt-2">
              Luxe Geuren
            </p>
            
            {/* Loading indicator */}
            <div className="mt-8 flex space-x-1">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for letter-by-letter shimmer effects */}
      <style>{`
        .zuiver-container {
          display: inline-flex;
          position: relative;
        }
        
        .zuiver-letter {
          display: inline-block;
          color: #D4AF37;
          text-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
          animation: letterGlow 3s ease-in-out infinite;
        }
        
        @keyframes letterGlow {
          0% {
            color: #D4AF37;
            text-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
          }
          15% {
            color: #FFE566;
            text-shadow: 
              0 0 10px rgba(255, 229, 102, 0.8),
              0 0 20px rgba(255, 229, 102, 0.6),
              0 0 30px rgba(255, 215, 0, 0.5),
              0 0 40px rgba(255, 215, 0, 0.3);
          }
          30% {
            color: #FFFACD;
            text-shadow: 
              0 0 15px rgba(255, 250, 205, 1),
              0 0 30px rgba(255, 250, 205, 0.8),
              0 0 45px rgba(255, 215, 0, 0.6),
              0 0 60px rgba(212, 175, 55, 0.4);
          }
          45% {
            color: #FFE566;
            text-shadow: 
              0 0 10px rgba(255, 229, 102, 0.8),
              0 0 20px rgba(255, 229, 102, 0.6),
              0 0 30px rgba(255, 215, 0, 0.5),
              0 0 40px rgba(255, 215, 0, 0.3);
          }
          60%, 100% {
            color: #D4AF37;
            text-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
          }
        }
        
        .logo-glow {
          filter: drop-shadow(0 0 15px rgba(212, 175, 55, 0.5));
        }
        
        .diamond-sparkle {
          animation: diamondGlow 2s ease-in-out infinite alternate;
        }
        
        @keyframes diamondGlow {
          0% {
            filter: drop-shadow(0 0 3px rgba(255, 215, 0, 0.5));
          }
          100% {
            filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.9));
          }
        }
        
        .jewel-glow {
          animation: jewelSparkle 1.5s ease-in-out infinite;
        }
        
        @keyframes jewelSparkle {
          0%, 100% {
            opacity: 0.7;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.3);
          }
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
